import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Eye, Plus, Filter, FolderOpen, Trash2, Undo2, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import usePersistedSearchParams from '../hooks/usePersistedSearchParams';
import DataTable from '../components/DataTable';
import ClientModal from '../components/ClientModal';
import CreateClientModal from '../components/CreateClientModal';
import MasavUploadModal from '../components/MasavUploadModal';
import MasavSummaryModal from '../components/MasavSummaryModal';
import { masavApi, handleMasavApiError, type MasavPreviewResult } from '../services/masavService';
import IconButton from '../components/common/IconButton';
import ConfirmDialog from '../components/ConfirmDialog';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import StatusFilterTabs from '../components/leads/StatusFilterTabs';
import ClientRoleIndicatorChip from '../components/clients/ClientRoleIndicatorChip';
import ClientFilterDrawer from '../components/clients/ClientFilterDrawer';
import StatusCellWithTooltip from '../components/clients/StatusCellWithTooltip';
import {
  ClientDto,
  ClientFilterDto,
  clientsApi,
  handleApiError,
  formatDate
} from '../services/clientsService';
import {
  statusesApi,
  diseasesApi,
  leadSourcesApi,
  departmentsApi,
  StatusDto,
  DiseaseDto,
  LeadSourceDto,
  LeadSourceType,
  DepartmentDto
} from '../services/settingsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AdvancedFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const BulkActions = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 12px 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const BulkInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const BulkButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BulkSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const BulkButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 8px 14px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

// Hit area around the row checkbox; stops the click from opening the client file
const SelectCell = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const ClientsPage: React.FC = () => {
  const { isManager, isStaff, canViewClientFinance } = useAuth();
  const navigate = useNavigate();
  const { initialParams, persistSearchParams } = usePersistedSearchParams('clients');

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [diseases, setDiseases] = useState<DiseaseDto[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);

  // Pagination - read from URL (or the remembered list view)
  const [currentPage, setCurrentPage] = useState(() => {
    const page = initialParams.get('page');
    return page ? parseInt(page) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = initialParams.get('pageSize');
    return size ? parseInt(size) : 25;
  });
  const [totalCount, setTotalCount] = useState(0);

  // Filters - read from URL (or the remembered list view)
  const [searchTerm, setSearchTerm] = useState(() => initialParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const statuses = initialParams.get('statuses');
    return statuses ? statuses.split(',') : [];
  });
  const [departmentIds, setDepartmentIds] = useState<number[]>(() => {
    const d = initialParams.get('department');
    return d ? d.split(',').map(x => parseInt(x)).filter(x => !isNaN(x)) : [];
  });
  const [diseaseFilter, setDiseaseFilter] = useState<string>(() => initialParams.get('disease') || '');
  // P8b: פילטר סוכן/ארגון (יחיד; '' = הכל, '-1' = ללא)
  const [agentFilter, setAgentFilter] = useState<string>(() => initialParams.get('agent') || '');
  const [organizationFilter, setOrganizationFilter] = useState<string>(() => initialParams.get('organization') || '');
  const [dateFromFilter, setDateFromFilter] = useState(() => initialParams.get('dateFrom') || '');
  const [dateToFilter, setDateToFilter] = useState(() => initialParams.get('dateTo') || '');

  // Bulk actions
  const [bulkAssignDepartment, setBulkAssignDepartment] = useState<string>('');

  // Modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showMasavModal, setShowMasavModal] = useState(false);
  const [showMasavSummary, setShowMasavSummary] = useState(false);
  const [masavFile, setMasavFile] = useState<File | null>(null);
  const [masavMonth, setMasavMonth] = useState('');
  const [masavPreview, setMasavPreview] = useState<MasavPreviewResult | null>(null);
  const [masavLoading, setMasavLoading] = useState(false);
  const [masavError, setMasavError] = useState<string | null>(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [viewingClient, setViewingClient] = useState<ClientDto | null>(null);

  // Delete / revert dialogs
  const [deletingClient, setDeletingClient] = useState<ClientDto | null>(null);
  const [revertingClient, setRevertingClient] = useState<ClientDto | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // מחיקה/החזרה-לליד/העברה — פעולות ניהוליות ל-Manager בלבד (תואם לאכיפת הבקנד).
  const canManageClient = useCallback((): boolean => isManager, [isManager]);

  // Update URL and remember the list view when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 25) params.set('pageSize', pageSize.toString());
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter.length > 0) params.set('statuses', statusFilter.join(','));
    if (departmentIds.length > 0) params.set('department', departmentIds.join(','));
    if (diseaseFilter) params.set('disease', diseaseFilter);
    if (agentFilter) params.set('agent', agentFilter);
    if (organizationFilter) params.set('organization', organizationFilter);
    if (dateFromFilter) params.set('dateFrom', dateFromFilter);
    if (dateToFilter) params.set('dateTo', dateToFilter);

    persistSearchParams(params);
  }, [currentPage, pageSize, searchTerm, statusFilter, departmentIds, diseaseFilter, agentFilter, organizationFilter, dateFromFilter, dateToFilter, persistSearchParams]);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams: ClientFilterDto = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || undefined,
        statusIds: statusFilter.length > 0 ? statusFilter.map(id => parseInt(id)) : undefined,
        diseaseIds: diseaseFilter ? [parseInt(diseaseFilter)] : undefined,
        // P8b: פילטר סוכן/ארגון יחיד (-1 = ללא)
        agentSourceId: agentFilter ? parseInt(agentFilter) : undefined,
        organizationSourceId: organizationFilter ? parseInt(organizationFilter) : undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        // multi-select; -1 = "ללא מחלקה" (מטופל בשרת). פילטר בלבד — לא מגביל נראות
        departmentIds: departmentIds.length > 0 ? departmentIds : undefined
      };

      const result = await clientsApi.getAll(filterParams);
      setClients(result.clients);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, departmentIds, diseaseFilter, agentFilter, organizationFilter, dateFromFilter, dateToFilter]);

  const loadInitialData = useCallback(async () => {
    try {
      // רשימת המחלקות נטענת תמיד — סינון לפי מחלקה זמין לכל הצוות (כולל מחלקות מושבתות לאיתור תיקים)
      try {
        const departmentsData = await departmentsApi.getAll();
        setDepartments(departmentsData);
      } catch (err) {
        console.error('Error loading departments:', err);
      }

      // רשימת מקורות הליד נטענת תמיד — הסינון לפי מקור זמין לכל המשתמשים
      try {
        const leadSourcesData = await leadSourcesApi.getAll();
        setLeadSources(leadSourcesData);
      } catch (err) {
        console.error('Error loading lead sources:', err);
      }

      const [statusesData, diseasesData] = await Promise.all([
        statusesApi.search('', 2),
        diseasesApi.getAll(),
      ]);

      setStatuses(statusesData);
      setDiseases(diseasesData);
    } catch (err) {
      setError(handleApiError(err));
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setDepartmentIds([]);
    setDiseaseFilter('');
    setAgentFilter('');
    setOrganizationFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setCurrentPage(1);
  };

  const handleAdvancedFilter = (filters: {
    departmentIds: number[];
    diseaseFilter: string;
    agentFilter: string;
    organizationFilter: string;
    dateFrom: string;
    dateTo: string;
  }) => {
    setDepartmentIds(filters.departmentIds);
    setDiseaseFilter(filters.diseaseFilter);
    setAgentFilter(filters.agentFilter);
    setOrganizationFilter(filters.organizationFilter);
    setDateFromFilter(filters.dateFrom);
    setDateToFilter(filters.dateTo);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleSelectClient = (clientId: number) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(client => client.id));
    }
  };

  // שיוך מחלקה המוני (Manager בלבד). ערך '-1' => ניקוי מחלקה
  const handleBulkAssignDepartment = async () => {
    if (!bulkAssignDepartment || selectedClients.length === 0) return;

    try {
      const departmentId = bulkAssignDepartment === '-1' ? null : parseInt(bulkAssignDepartment);
      const result = await clientsApi.bulkAssignDepartment({
        clientIds: selectedClients,
        departmentId
      });

      if (result.success) {
        toast.success(`${result.updatedCount} לקוחות שויכו למחלקה`);
        setSelectedClients([]);
        setBulkAssignDepartment('');
        loadClients();
      } else {
        setError(`שיוך מחלקה נכשל: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleViewClient = async (client: ClientDto) => {
    try {
      const fullClient = await clientsApi.getById(client.id);
      setViewingClient(fullClient);
      setShowClientModal(true);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleManageClient = (client: ClientDto) => {
    navigate(`/clients/${client.id}/manage`);
  };

  const handleConfirmDelete = async () => {
    if (!deletingClient) return;
    try {
      setActionLoading(true);
      await clientsApi.deleteClient(deletingClient.id);
      setDeletingClient(null);
      loadClients();
      loadInitialData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRevert = async () => {
    if (!revertingClient) return;
    try {
      setActionLoading(true);
      await clientsApi.revertToLead(revertingClient.id);
      setRevertingClient(null);
      loadClients();
      loadInitialData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClientCreated = () => {
    loadClients();
    loadInitialData();
  };

  const handleMasavContinue = async (payload: { file: File; selectedMonth: string }) => {
    setShowMasavModal(false);
    setMasavFile(payload.file);
    setMasavMonth(payload.selectedMonth);
    setMasavPreview(null);
    setMasavError(null);
    setMasavLoading(true);
    setShowMasavSummary(true);
    try {
      const result = await masavApi.preview(payload.file, payload.selectedMonth);
      setMasavPreview(result);
    } catch (err) {
      setMasavError(`שגיאה בעיבוד הקובץ: ${handleMasavApiError(err)}`);
    } finally {
      setMasavLoading(false);
    }
  };

  // Dynamic page title
  const getPageTitle = () => {
    return isManager ? 'ניהול לקוחות' : 'לקוחות';
  };

  const columns = [
    // עמודת בחירה — Manager בלבד, בשני הטאבים (שיוך מחלקה המוני זמין ברשימה הראשית)
    ...(isManager ? [{
      key: 'select',
      title: 'בחירה',
      width: '60px',
      render: (_: any, record: ClientDto) => (
        <SelectCell onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedClients.includes(record.id)}
            onChange={() => handleSelectClient(record.id)}
          />
        </SelectCell>
      )
    }] : []),
    {
      key: 'fullName',
      title: 'שם מלא',
      width: '180px'
    },
    {
      key: 'idNumber',
      title: 'תעודת זהות',
      width: '120px'
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '130px'
    },
    {
      key: 'currentStatusName',
      title: 'סטטוס',
      width: '140px',
      render: (value: string, record: ClientDto) => (
        <StatusCellWithTooltip
          currentStatusName={value}
          currentStatusColor={record.currentStatusColor}
          currentStages={record.currentStages}
        />
      )
    },
    {
      key: 'primaryDiseaseName',
      title: 'מחלה עיקרית',
      width: '140px',
      render: (value: string) => value || '-'
    },
    {
      key: 'departmentName',
      title: 'מחלקה',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'agentSourceName',
      title: 'סוכן',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'organizationSourceName',
      title: 'ארגון',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'contractSignedDate',
      title: 'תאריך חתימה',
      width: '120px',
      render: (value: string) => value ? formatDate(value) : '-'
    },
    {
      key: 'daysInTreatment',
      title: 'ימים בטיפול',
      width: '100px',
      render: (value: number) => value > 0 ? `${value}` : '-'
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '220px',
      render: (_: any, record: ClientDto) => (
        <ActionsCell onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon={Eye}
            onClick={(e) => { e.stopPropagation(); handleViewClient(record); }}
            title="צפייה בלקוח"
          />
          <IconButton
            icon={FolderOpen}
            onClick={(e) => { e.stopPropagation(); handleManageClient(record); }}
            title="ניהול תיק"
            variant="primary"
          />
          {canManageClient() && record.originalLeadId != null && (
            <IconButton
              icon={Undo2}
              onClick={(e) => { e.stopPropagation(); setRevertingClient(record); }}
              title="החזרה לליד"
            />
          )}
          {canManageClient() && (
            <IconButton
              icon={Trash2}
              onClick={(e) => { e.stopPropagation(); setDeletingClient(record); }}
              title="מחיקת לקוח"
              variant="danger"
            />
          )}
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>
            {getPageTitle()}
          </PageTitle>
          <ClientRoleIndicatorChip />
        </HeaderRight>
        <HeaderActions>
          {canViewClientFinance && (
            <Button variant="primary" onClick={() => setShowMasavModal(true)}>
              <Upload size={16} />
              העלאת קובץ תשלומי מס"ב
            </Button>
          )}
          {isStaff && (
            <Button variant="primary" onClick={() => setShowCreateClientModal(true)}>
              <Plus size={16} />
              לקוח חדש
            </Button>
          )}
        </HeaderActions>
      </PageHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="חיפוש לפי שם, תעודת זהות או טלפון..."
        />

        <FilterRow>
          <StatusFilterTabs
            statuses={statuses}
            selectedStatuses={statusFilter}
            onChange={(values) => {
              setStatusFilter(values);
              setCurrentPage(1);
            }}
          />
          <AdvancedFilterButton onClick={() => setShowFilterDrawer(true)}>
            <Filter size={16} />
            מסננים מתקדמים
          </AdvancedFilterButton>
        </FilterRow>
      </FilterContainer>

      <ClientFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApply={handleAdvancedFilter}
        onClear={handleClearFilters}
        filters={{
          departmentIds,
          diseaseFilter,
          agentFilter,
          organizationFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter
        }}
        diseases={diseases}
        departments={departments.filter(d => d.isActive)}
        agents={leadSources.filter(s => s.type === LeadSourceType.Agent)}
        organizations={leadSources.filter(s => s.type === LeadSourceType.Organization)}
      />

      {isManager && selectedClients.length > 0 && (
        <BulkActions>
          <BulkInfo>
            נבחרו {selectedClients.length} לקוחות
          </BulkInfo>
          <BulkButtons>
            {/* שיוך מחלקה */}
            <BulkSelect
              value={bulkAssignDepartment}
              onChange={(e) => setBulkAssignDepartment(e.target.value)}
            >
              <option value="">בחר מחלקה</option>
              <option value="-1">ללא מחלקה</option>
              {departments.filter(d => d.isActive).map(dep => (
                <option key={dep.id} value={dep.id}>
                  {dep.name}
                </option>
              ))}
            </BulkSelect>
            <BulkButton
              variant="primary"
              onClick={handleBulkAssignDepartment}
              disabled={!bulkAssignDepartment}
            >
              שייך למחלקה
            </BulkButton>
          </BulkButtons>
        </BulkActions>
      )}

      <DataTable
        data={clients}
        columns={columns}
        loading={loading}
        onRowClick={handleManageClient}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: handlePageChange
        }}
        actions={
          isManager ? (
            <input
              type="checkbox"
              checked={selectedClients.length === clients.length && clients.length > 0}
              onChange={handleSelectAll}
              title="בחר הכל"
            />
          ) : undefined
        }
      />

      <ClientModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setViewingClient(null);
        }}
        client={viewingClient}
      />

      <CreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onClientCreated={handleClientCreated}
      />

      <MasavUploadModal
        isOpen={showMasavModal}
        onClose={() => setShowMasavModal(false)}
        onContinue={handleMasavContinue}
      />

      <MasavSummaryModal
        isOpen={showMasavSummary}
        onClose={() => setShowMasavSummary(false)}
        file={masavFile}
        month={masavMonth}
        preview={masavPreview}
        loading={masavLoading}
        error={masavError}
        onCommitted={() => loadClients()}
      />

      <ConfirmDialog
        isOpen={deletingClient !== null}
        onClose={() => setDeletingClient(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת לקוח"
        message={
          <span>
            האם אתה בטוח שברצונך למחוק את הלקוח <strong>{deletingClient?.fullName}</strong>?
            <br /><br />
            הלקוח יוסר מרשימת הלקוחות, אך כל הנתונים (פגישות, תשלומים, מסמכים) יישמרו במערכת לצורך תיעוד.
          </span>
        }
        confirmText="מחק לקוח"
        cancelText="ביטול"
        type="danger"
        loading={actionLoading}
      />

      <ConfirmDialog
        isOpen={revertingClient !== null}
        onClose={() => setRevertingClient(null)}
        onConfirm={handleConfirmRevert}
        title="החזרה לרשימת הלידים"
        message={
          <span>
            <strong>אזהרה חמורה</strong> - פעולה זו תמחק לצמיתות את כל הנתונים של הלקוח{' '}
            <strong>{revertingClient?.fullName}</strong>:
            <br /><br />
            • כל הפגישות והתורים
            <br />
            • כל רישומי הוועדות
            <br />
            • כל התשלומים ותוכניות התשלום
            <br />
            • כל החיובים והעמלות
            <br />
            • כל המסמכים והתיקיות
            <br />
            • כל שלבי התהליך והמידע הרפואי
            <br /><br />
            החוזה החתום יבוטל והליד יוחזר לרשימת הלידים הפתוחים.
            <br /><br />
            <strong>פעולה זו אינה ניתנת לשחזור!</strong>
          </span>
        }
        confirmText="החזר לליד ומחק נתונים"
        cancelText="ביטול"
        type="danger"
        loading={actionLoading}
      />
    </PageContainer>
  );
};

export default ClientsPage;
